import { defineStore } from "pinia";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";
import { SetupHubStatus, SetupHubStepCategory, SetupHubStep } from "@/constants/setup-hub.ts";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";

export interface SetupHubStepItem {
  step: SetupHubStep;
  status: SetupHubStatus;
  dependentSteps: SetupHubStep[];
  dependentCompleted: boolean;
}

export type SetupHubSteps = Record<SetupHubStepCategory, SetupHubStepItem[]>;

export interface SetupHubData {
  status: SetupHubStatus;
  steps: SetupHubSteps;
  dismissed: boolean;
}

interface SetupHubState {
  data: SetupHubData | null;
  loading: boolean;
  stepCompletedFromWebsocket: SetupHubStep | null;
}

const defaultSetupHubState: SetupHubState = {
  data: null,
  loading: false,
  stepCompletedFromWebsocket: null,
};

export const useSetupHubStore = defineStore(StoreModule.SETUP_HUB, {
  state: (): SetupHubState => ({ ...defaultSetupHubState }),
  getters: {
    totalSteps: (state): number => {
      if (!state.data?.steps) {
        return 0;
      }
      const completedSteps = Object.values(state.data.steps).reduce(
        (total, steps) => total + steps.length,
        0
      );
      // this is done to have at least something green in progress bar at the beginning
      return completedSteps > 0 ? completedSteps : 1;
    },
    completedSteps: (state): number => {
      if (!state.data?.steps) {
        return 0;
      }
      return Object.values(state.data.steps).reduce(
        (total, steps) => total + steps.filter((step) => step.status !== SetupHubStatus.NEW).length,
        0
      );
    },
  },
  actions: {
    async getOnboardingStatus() {
      this.loading = true;
      try {
        const response = await axiosInstance.request<SetupHubData>(api.getOnboardingStatus());
        this.data = response.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async skipOnboardingStep(step: SetupHubStep) {
      try {
        await axiosInstance.request(api.skipOnboardingStep(step));
        await this.getOnboardingStatus();
      } catch (error) {
        console.error(error);
      }
    },
    async dismissOnboarding() {
      try {
        await axiosInstance.request(api.dismissOnboarding());
        await this.getOnboardingStatus();
        useSidebarPanelStore().closeSidebar();
      } catch (error) {
        console.error(error);
      }
    },
    async restoreOnboarding() {
      try {
        await axiosInstance.request(api.restoreOnboarding());
        await this.getOnboardingStatus();
      } catch (error) {
        console.error(error);
      }
    },
    completeStepFromWebsocket(step: SetupHubStep) {
      this.stepCompletedFromWebsocket = step;
      setTimeout(() => {
        this.stepCompletedFromWebsocket = null;
      }, 5000);
    },
    findStep(step: SetupHubStep) {
      if (!this.data?.steps) {
        return null;
      }

      for (const categorySteps of Object.values(this.data.steps)) {
        const foundStep = categorySteps.find((stepItem) => stepItem.step === step);
        if (foundStep) {
          return foundStep;
        }
      }

      return null;
    },
  },
});
